import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ResetPasswordPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(token ? "" : "This reset link is missing its token. Use the full link from your email.");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center p-8" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-[var(--bg-card)]/50 backdrop-blur-xl border border-[var(--border-strong)] rounded-xl p-8">
          <img src="/web_logo.svg" alt="Quibn" className="h-10 w-auto mb-6" />
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[var(--text-primary)] mb-2">
            Choose a new password
          </h2>

          {done ? (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-400" data-testid="reset-success">
              Password updated. Taking you to sign in...
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400" data-testid="reset-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-3 mt-4">
                <Input
                  type="password" value={password} required minLength={8}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (8+ characters)"
                  autoComplete="new-password"
                  className="h-11 bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                  data-testid="reset-password-input"
                />
                <Input
                  type="password" value={confirm} required minLength={8}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  className="h-11 bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                  data-testid="reset-confirm-input"
                />
                <Button
                  type="submit"
                  disabled={submitting || !token}
                  className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold rounded-md h-12"
                  data-testid="reset-submit"
                >
                  {submitting ? "Updating..." : "Set new password"}
                </Button>
              </form>
            </>
          )}

          <p className="text-center text-xs text-[var(--text-muted)] mt-6">
            <Link to="/login" className="hover:text-[var(--text-secondary)] transition-colors">&larr; Back to sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
